import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@components/ui";
import styles from "./ErrorBoundary.module.css";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level React error boundary. A render error anywhere in the tree would
 * otherwise unmount the whole app and leave a blank screen; here we catch it and
 * show a friendly recover-by-reload fallback that reuses the Aria design tokens.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for diagnostics; the fallback keeps the app usable.
    console.error("Aria hit an unexpected error:", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className={styles.root}>
        <div className={styles.card}>
          <img src="/icons/icon.svg" alt="" className={styles.logo} width={64} height={64} />
          <div className={styles.brand}>Aria</div>
          <h1 className={styles.title}>Something went wrong</h1>
          <p className={styles.description}>
            Aria ran into an unexpected error. Reloading usually clears it up.
          </p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </div>
    );
  }
}
